/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { ActionButton, Pivot, PivotItem, Spinner, SpinnerSize, Stack } from "@fluentui/react";

import * as UserProfileActions from "Dataflows/UserProfile/UserProfileAction";
import * as UserProfileSelector from "Dataflows/UserProfile/UserProfileSelector";

import ProfileHeader from "Components/ProfileHeader/ProfileHeader";
import StudentBenefitDetails from "Components/StudentBenefitDetails/StudentBenefitDetails";
import StudentComments from "Components/StudentComments/StudentComments";
import StudentProfile from "Components/StudentProfile/StudentProfile";
import StudentProgramProfile from "Components/StudentProgramProfile/StudentProgramProfile";
import { ProfileTabs } from "Constants/ProfileConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { DetailWrapper } from "Containers/Common/CommonContainer.styles";
import { AppState } from "Dataflows/Reducers";
import { useProfileTabs } from "Utility/CustomTabHooks";
import * as MLSABenefitsSelector from "Dataflows/UserProfile/UserProfileSelector";

import {
  containerStackTokens,
  wrapperStackStyles,
} from "../../Containers/MVP/MostValuableProfessionalDetailsContainer/MostValuableProfessionalDetailsContainer.styles";
import { OperationStatus } from "Constants/OperationStatus";

/**
 * Student Details functional component
 * @param props {IStudentDetailsProps} Student Details props.
 * @returns {React.ReactElement} Student Details Component.
 */
const StudentDetails = (): React.ReactElement => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const userProfileId = Number.parseInt(id);
  const dispatch = useDispatch();
  const { selectedTab, setSelectedTab } = useProfileTabs();

  const claimingBenefitError = useSelector(MLSABenefitsSelector.getIsClaimingBenefitsError);
  const isClaimingBenefitStatus = useSelector(MLSABenefitsSelector.getIsClaimingBenefitStatus);
  const profileBenefits = useSelector(MLSABenefitsSelector.getProfileBenefits);

  useEffect(() => {
    if (userProfileId) {
      dispatch(UserProfileActions.updateSelectedUserProfileId(userProfileId));
      dispatch(UserProfileActions.loadUserProfile(userProfileId));
    }
  }, [userProfileId]);

  const isCurrentUserProfileLoading = useSelector((state: AppState) =>
    UserProfileSelector.isCurrentUserProfileLoading(state.userProfileState)
  );
  const selectedUserProfile = useSelector((state: AppState) =>
    UserProfileSelector.getUserProfile(state.userProfileState)
  );

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    history.goBack();
  };

  return (
    <>
      <Stack styles={wrapperStackStyles}>
        <Stack>
          <ActionButton
            iconProps={{ iconName: "RevToggleKey" }}
            ariaLabel="Back"
            onClick={onBackClickEvent}
          >
            Back
          </ActionButton>
        </Stack>
        {isCurrentUserProfileLoading ? (
          <Spinner size={SpinnerSize.large} />
        ) : (
          <Stack tokens={containerStackTokens}>
            <ProfileHeader
              userProfileId={selectedUserProfile.id}
              firstName={selectedUserProfile.firstName}
              lastName={selectedUserProfile.lastName}
              primaryEmail={selectedUserProfile.primaryEmail}
              secondaryEmail={selectedUserProfile.secondaryEmail}
              tenants={selectedUserProfile.tenants}
              currentTenant={TenantTypesEnum.MSP}
              isPrivate={selectedUserProfile?.isPrivate}
            />
            <Stack>
              <DetailWrapper>
                <Pivot
                  selectedKey={selectedTab}
                  onLinkClick={(item: PivotItem | undefined): void => {
                    setSelectedTab((item?.props.itemKey as ProfileTabs) || ProfileTabs.Profile);
                  }}
                >
                  <PivotItem headerText="Profile" itemKey={ProfileTabs.Profile}>
                    <StudentProfile />
                  </PivotItem>
                  <PivotItem
                    headerText="Program Information"
                    itemKey={ProfileTabs.ProgramInformation}
                  >
                    <StudentProgramProfile
                      userProfileId={selectedUserProfile.id}
                      microsoftReferralId={selectedUserProfile.microsoftReferralId}
                    />
                  </PivotItem>
                  <PivotItem headerText="Benefits" itemKey={ProfileTabs.Benefits}>
                    <StudentBenefitDetails
                      profileBenefits={profileBenefits}
                      claimingBenefitError={claimingBenefitError}
                      isClaimingBenefitStatus={isClaimingBenefitStatus as OperationStatus}
                      claimBenefitReset={UserProfileActions.claimBenefitReset}
                      claimBenefit={UserProfileActions.claimBenefit}
                      getProfileBenefits={UserProfileActions.getProfileBenefits}
                      tenantName={TenantTypesEnum.MLSA}
                    />
                  </PivotItem>
                  <PivotItem headerText="Comments" itemKey={ProfileTabs.Comments}>
                    <StudentComments />
                  </PivotItem>
                </Pivot>
              </DetailWrapper>
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default StudentDetails;
